import React, { useState } from 'react';
import { Stack, Paper, Typography, IconButton, Collapse, Box } from '@mui/material';
import {
  DragIndicator,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useAppSelector } from '../../../../app/hooks';
import { STEP_TYPE_LABELS, WorkflowStepType } from '../../../../services/WorkflowService';
import { StepTypeSelect } from './fields';
import { StepNameField } from './fields';
import { StepDescriptionField } from './fields';
import { StepOrderBadge } from './display';
import { StepConfigurationPanel } from './StepConfigurationPanel';
import { DeleteStepButton } from './actions';

interface StepListItemProps {
  index: number;
  dragId: string;
}

const stepTypeLabel = (type: string) => STEP_TYPE_LABELS[type as WorkflowStepType] ?? type;

export const StepListItem: React.FC<StepListItemProps> = ({ index, dragId }) => {
  const step = useAppSelector((state) => state.workflow.currentWorkflow?.steps[index]);
  const [expanded, setExpanded] = useState(true);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: dragId });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  if (!step) return null;

  return (
    <Paper
      ref={setNodeRef}
      style={style}
      elevation={isDragging ? 4 : 1}
      sx={{
        p: 0,
        backgroundColor: '#ffffff',
        border: '1px solid #e2e8f0',
        overflow: 'hidden',
      }}
    >
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{ px: 2, py: 1.5, cursor: 'pointer', '&:hover': { backgroundColor: '#f8fafc' } }}
        onClick={() => setExpanded((prev) => !prev)}
      >
        <Box
          {...attributes}
          {...listeners}
          onClick={(e) => e.stopPropagation()}
          sx={{ display: 'flex', cursor: 'grab', color: '#94a3b8', '&:active': { cursor: 'grabbing' } }}
        >
          <DragIndicator fontSize="small" />
        </Box>

        <StepOrderBadge index={index} />

        <Typography
          variant="body2"
          sx={{
            backgroundColor: '#eff6ff',
            color: '#3b82f6',
            px: 1,
            py: 0.25,
            borderRadius: 1,
            fontWeight: 600,
            fontSize: '0.75rem',
            whiteSpace: 'nowrap',
          }}
        >
          {stepTypeLabel(step.stepType)}
        </Typography>

        <Typography variant="body2" sx={{ fontWeight: 500, color: '#1e293b', flex: 1 }} noWrap>
          {step.name || 'Unnamed Step'}
        </Typography>

        <IconButton size="small" onClick={(e) => { e.stopPropagation(); setExpanded((prev) => !prev); }}>
          {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </IconButton>

        <Box onClick={(e) => e.stopPropagation()}>
          <DeleteStepButton index={index} />
        </Box>
      </Stack>

      <Collapse in={expanded}>
        <Stack spacing={2} sx={{ px: 2, pb: 2 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
            <StepTypeSelect index={index} />
            <StepNameField index={index} />
          </Stack>
          <StepDescriptionField index={index} />
          <StepConfigurationPanel index={index} />
        </Stack>
      </Collapse>
    </Paper>
  );
};

export default StepListItem;
